import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  styled,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  PlayCircleFilled as InProgressIcon,
  Cancel as CancelledIcon,
  Flag as FlagIcon,
  PhotoCamera as PhotoCameraIcon,
  Euro as EuroIcon,
  MoneyOff as MoneyOffIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  SettingsApplications as PartsIcon,
  Speed as SpeedIcon,
  LocalGasStation as EngineIcon,
  AirportShuttle as BodyworkIcon,
  Security as SafetyIcon,
  Memory as ElectronicsIcon,
  BrokenImage as BrakingIcon,
  Timeline as SuspensionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShowChart as ChartIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { car as carApi, task as taskApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import { format } from 'date-fns';
import { filestackClient } from '../../utils/filestack';
import CreateTaskModal from '../workshop/components/CreateTaskModal';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip as ChartTooltip, 
  Legend 
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getTransformedImageUrl } from '../../utils/filestack';

import CarDetailsCard from './components/CarDetailsCard';
import CarComponentsStatus from './components/CarComponentsStatus';
import CarTasksSection from './components/CarTasksSection';
import CarReplacementsSection from './components/CarReplacementsSection';
import CarRaceHistory from './components/CarRaceHistory';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

// Create a styled IconButton for the expand/collapse functionality
const ExpandButton = styled(IconButton)(({ theme }) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const CarDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['cars', 'common', 'workshop']);
  const { user } = useAuth();
  
  const [car, setCar] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [checkupTaskMode, setCheckupTaskMode] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    model: '',
    year: '',
    imageUrl: '',
    notes: '',
    checkupThreshold: 5000,
    components: {
      brakePads: {
        lastReplaced: 0,
        lifespan: 30000,
        notes: ''
      },
      brakeDiscs: {
        lastReplaced: 0,
        lifespan: 60000,
        notes: ''
      },
      frontTires: {
        lastReplaced: 0,
        lifespan: 40000,
        notes: ''
      },
      rearTires: {
        lastReplaced: 0,
        lifespan: 40000,
        notes: ''
      },
      motor: {
        lastReplaced: 0,
        lifespan: 200000,
        notes: ''
      }
    }
  });
  const [saving, setSaving] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  // New state variables for replacements
  const [replacementTypes, setReplacementTypes] = useState([]);
  const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
  const [replacementFormData, setReplacementFormData] = useState({
    title: '',
    description: '',
    type: '',
    subtype: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });
  const [editingReplacementId, setEditingReplacementId] = useState(null);
  const [loadingReplacementTypes, setLoadingReplacementTypes] = useState(false);
  const [deleteReplacementDialogOpen, setDeleteReplacementDialogOpen] = useState(false);
  const [replacementToDelete, setReplacementToDelete] = useState(null);

  // Add state for expanded sections
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [replacementsExpanded, setReplacementsExpanded] = useState(false);

  // New state variables for race history
  const [raceHistory, setRaceHistory] = useState([]);
  const [loadingRaceHistory, setLoadingRaceHistory] = useState(false);
  const [showRaceHistory, setShowRaceHistory] = useState(false);

  // New state variables for components status
  const [showComponents, setShowComponents] = useState(false);

  // Chart data for race history
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: t('carDetail.kilometers'),
        data: [],
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1
      },
      {
        label: t('carDetail.cumulativeKilometers'),
        data: [],
        fill: false,
        borderColor: 'rgb(255, 99, 132)',
        tension: 0.1
      }
    ]
  });

  const [chartOptions, setChartOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t('carDetail.kilometers')
        }
      },
      x: {
        title: {
          display: true,
          text: t('carDetail.event')
        }
      }
    }
  });

  useEffect(() => {
    fetchCarDetails();
    fetchReplacementTypes();
    fetchRaceHistory();
  }, [id]);

  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      const carData = await carApi.getById(id);
      setCar(carData.car);
      
      // Debug car ownership and data
      console.log('Current user:', user);
      console.log('Car data:', carData.car);
      console.log('Car owner ID:', carData.car.user?._id);
      console.log('Is owner check:', user?._id === carData.car.user?._id);
      console.log('Car model:', carData.car.model);
      console.log('Car year:', carData.car.year);
      
      // Initialize edit form data
      setEditFormData({
        name: carData.car.name || '',
        model: carData.car.model || '',
        year: carData.car.year || '',
        imageUrl: carData.car.imageUrl || '',
        notes: carData.car.notes || '',
        checkupThreshold: carData.car.checkupThreshold || 5000,
        components: {
          brakePads: {
            lastReplaced: carData.car.components?.brakePads?.lastReplaced || 0,
            lifespan: carData.car.components?.brakePads?.lifespan || 30000,
            notes: carData.car.components?.brakePads?.notes || ''
          },
          brakeDiscs: {
            lastReplaced: carData.car.components?.brakeDiscs?.lastReplaced || 0,
            lifespan: carData.car.components?.brakeDiscs?.lifespan || 60000,
            notes: carData.car.components?.brakeDiscs?.notes || ''
          },
          frontTires: {
            lastReplaced: carData.car.components?.frontTires?.lastReplaced || 0,
            lifespan: carData.car.components?.frontTires?.lifespan || 40000,
            notes: carData.car.components?.frontTires?.notes || ''
          },
          rearTires: {
            lastReplaced: carData.car.components?.rearTires?.lastReplaced || 0,
            lifespan: carData.car.components?.rearTires?.lifespan || 40000,
            notes: carData.car.components?.rearTires?.notes || ''
          },
          motor: {
            lastReplaced: carData.car.components?.motor?.lastReplaced || 0,
            lifespan: carData.car.components?.motor?.lifespan || 200000,
            notes: carData.car.components?.motor?.notes || ''
          }
        }
      });
      
      // Fetch tasks for this car
      const tasksData = await taskApi.getByCarId(id);
      console.log('Tasks data:', tasksData);
      console.log('Tasks with financial tracking:', tasksData.tasks?.filter(task => task.financialTracking));
      setTasks(tasksData.tasks || []);
    } catch (error) {
      console.error('Error fetching car details:', error);
      setError(t('errors.loadingCarDetails'));
      toast.error(t('errors.loadingCarDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    
    // Check if this is a component field (format: components.componentName.property)
    if (name.startsWith('components.')) {
      const [_, componentName, property] = name.split('.');
      
      // Convert to number if it's a numeric field (but not for notes)
      const processedValue = property !== 'notes' && value !== '' ? parseInt(value, 10) : value;
      
      setEditFormData(prev => ({
        ...prev,
        components: {
          ...prev.components,
          [componentName]: {
            ...prev.components[componentName],
            [property]: processedValue
          }
        }
      }));
    } else {
      // Handle regular fields as before
      // For checkupThreshold, preserve empty string to allow user to clear the field
      let processedValue;
      if (name === 'year' && value !== '') {
        processedValue = parseInt(value, 10);
      } else if (name === 'checkupThreshold') {
        processedValue = value === '' ? '' : parseInt(value, 10);
      } else {
        processedValue = value;
      }
      
      setEditFormData({
        ...editFormData,
        [name]: processedValue
      });
    }
    
    console.log('Updated form data:', editFormData);
  };

  const handleImageUpload = async () => {
    try {
      console.log('Opening filestack picker for image upload');
      setSaving(true);
      
      await filestackClient.picker({
        accept: ['image/*'],
        maxFiles: 1,
        uploadInBackground: false,
        onUploadDone: async (res) => {
          if (res.filesUploaded && res.filesUploaded.length > 0) {
            const imageUrl = res.filesUploaded[0].url;
            console.log('Image uploaded successfully:', imageUrl);
            
            setEditFormData({
              ...editFormData,
              imageUrl
            });
            
            // Immediately update the car with the new image
            await handleUpdateCarImage(imageUrl);
          } else {
            console.error('No files uploaded or upload response is invalid');
            toast.error(t('errors.updatingImage'));
          }
        }
      }).open();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('errors.updatingImage'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUploadError = (error) => {
    console.error('Image upload error in CarDetailPage:', error);
    toast.error(t('errors.updatingImage'));
  };

  const handleUpdateCarImage = async (imageUrl) => {
    try {
      console.log('Updating car image with URL:', imageUrl);
      setSaving(true);
      await carApi.update(id, { imageUrl });
      toast.success(t('success.imageUpdated'));
      fetchCarDetails();
    } catch (error) {
      console.error('Error updating car image:', error);
      toast.error(t('errors.updatingImage'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      
      // Prepare the data to send to the server
      const updateData = {
        name: editFormData.name,
        model: editFormData.model || '',
        year: editFormData.year || null,
        color: editFormData.color || '',
        licensePlate: editFormData.licensePlate || '',
        imageUrl: editFormData.imageUrl || '',
        notes: editFormData.notes || '',
        checkupThreshold: editFormData.checkupThreshold === '' ? 5000 : (editFormData.checkupThreshold || 5000),
        components: editFormData.components
      };
      
      console.log('Saving car with data:', updateData);
      
      const response = await carApi.update(id, updateData);
      console.log('Car update response:', response);
      
      toast.success(t('success.carUpdated'));
      setEditDialogOpen(false);
      fetchCarDetails();
    } catch (error) {
      console.error('Error updating car:', error);
      toast.error(t('errors.updatingCar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCar = async () => {
    try {
      await carApi.delete(id);
      toast.success(t('success.carDeleted'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting car:', error);
      toast.error(t('errors.deletingCar'));
    }
  };

  const handleImageError = () => {
    setImageErrors(prev => ({
      ...prev,
      [car._id]: true
    }));
  };

  const getStatusIcon = (status, task) => {
    // Base icon based on status
    let icon;
    switch (status) {
      case 'completed':
        icon = <CompletedIcon color="success" />;
        break;
      case 'in_progress':
        icon = <InProgressIcon color="primary" />;
        break;
      case 'pending':
        icon = <PendingIcon color="warning" />;
        break;
      case 'cancelled':
        icon = <CancelledIcon color="error" />;
        break;
      default:
        icon = <PendingIcon color="warning" />;
    }
    
    // If there's no financial tracking or no repair cost, just return the icon
    if (!task || !task.financialTracking || !task.financialTracking.repairCost || task.financialTracking.repairCost <= 0) {
      return icon;
    }
    
    // Add a badge to the icon based on payment status
    return (
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          task.financialTracking.isPaid ? 
            <EuroIcon color="success" fontSize="small" sx={{ backgroundColor: 'white', borderRadius: '50%', padding: '2px' }} /> : 
            <MoneyOffIcon color="warning" fontSize="small" sx={{ backgroundColor: 'white', borderRadius: '50%', padding: '2px' }} />
        }
      >
        {icon}
      </Badge>
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low':
        return '#2196F3'; // Blue
      case 'medium':
        return '#4CAF50'; // Green
      case 'high':
        return '#FF9800'; // Orange
      case 'urgent':
        return '#F44336'; // Red
      default:
        return '#9E9E9E'; // Grey
    }
  };

  // Helper function to check if the current user is the car owner
  const isCarOwner = () => {
    return user && car && car.user && (user._id === car.user._id || user.isAdmin);
  };

  // Function to render payment status icon
  const getPaymentStatusIcon = (task) => {
    if (!task.financialTracking || !task.financialTracking.repairCost || task.financialTracking.repairCost <= 0) {
      return null;
    }
    
    return task.financialTracking.isPaid ? (
      <Tooltip title={t('workshop:taskDetail.financialTracking.paid')}>
        <Chip 
          icon={<EuroIcon />} 
          label={new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(task.financialTracking.repairCost)}
          size="small"
          color="success"
          variant="filled"
          sx={{ 
            ml: 1,
            fontWeight: 'bold',
            '& .MuiChip-icon': { color: 'inherit' }
          }}
        />
      </Tooltip>
    ) : (
      <Tooltip title={t('workshop:taskDetail.financialTracking.pending')}>
        <Chip 
          icon={<MoneyOffIcon />} 
          label={new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(task.financialTracking.repairCost)}
          size="small"
          color="warning"
          variant="filled"
          sx={{ 
            ml: 1,
            fontWeight: 'bold',
            '& .MuiChip-icon': { color: 'inherit' }
          }}
        />
      </Tooltip>
    );
  };

  const handleTaskCreated = () => {
    // Refresh the tasks list after a new task is created
    fetchCarDetails();
    setCreateTaskModalOpen(false);
    
    // If this was a checkup task, reset the kilometers since checkup
    if (checkupTaskMode) {
      // Reset the checkup task mode
      setCheckupTaskMode(false);
      
      // Reset the kilometers since checkup in the database
      resetKilometersSinceCheckup();
    }
  };
  
  // Function to reset kilometers since checkup
  const resetKilometersSinceCheckup = async () => {
    try {
      await carApi.update(id, { totalKilometersSinceCheckup: 0 });
      // Refresh car details to show the updated value
      fetchCarDetails();
    } catch (error) {
      console.error('Error resetting kilometers since checkup:', error);
      toast.error(t('errors.updatingCar'));
    }
  };

  // Filter tasks based on the toggle state
  const filteredTasks = tasks.filter(task => {
    // Always show tasks that are not completed
    if (task.status !== 'completed') {
      return true;
    }
    
    // For completed tasks, check if we should show them
    if (showCompletedTasks) {
      return true;
    }
    
    // Always show completed tasks with pending payment
    if (task.financialTracking && 
        task.financialTracking.repairCost > 0 && 
        !task.financialTracking.isPaid) {
      return true;
    }
    
    // Hide other completed tasks
    return false;
  });

  // Fetch replacement types
  const fetchReplacementTypes = async () => {
    try {
      setLoadingReplacementTypes(true);
      const types = await carApi.getReplacementTypes();
      setReplacementTypes(types);
    } catch (error) {
      console.error('Error fetching replacement types:', error);
      toast.error('Error fetching replacement types');
    } finally {
      setLoadingReplacementTypes(false);
    }
  };

  // Handle replacement form change
  const handleReplacementFormChange = (e) => {
    const { name, value } = e.target;
    setReplacementFormData({
      ...replacementFormData,
      [name]: value
    });

    // Reset subtype if type changes
    if (name === 'type') {
      setReplacementFormData(prev => ({
        ...prev,
        subtype: ''
      }));
    }
  };

  // Open replacement dialog for adding
  const handleOpenAddReplacementDialog = () => {
    setReplacementFormData({
      title: '',
      type: '',
      subtype: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      notes: ''
    });
    setEditingReplacementId(null);
    setReplacementDialogOpen(true);
  };

  // Open replacement dialog for editing
  const handleOpenEditReplacementDialog = (replacement) => {
    setReplacementFormData({
      title: replacement.title,
      type: replacement.type,
      subtype: replacement.subtype,
      date: format(new Date(replacement.date), 'yyyy-MM-dd'),
      notes: replacement.notes || ''
    });
    setEditingReplacementId(replacement._id);
    setReplacementDialogOpen(true);
  };

  // Close replacement dialog
  const handleCloseReplacementDialog = () => {
    setReplacementDialogOpen(false);
    setEditingReplacementId(null);
  };

  // Save replacement
  const handleSaveReplacement = async () => {
    try {
      setSaving(true);
      
      // Validate form data
      if (!replacementFormData.title || !replacementFormData.type || !replacementFormData.subtype) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      if (editingReplacementId) {
        // Update existing replacement
        await carApi.updateReplacement(id, editingReplacementId, replacementFormData);
        toast.success('Replacement updated successfully');
      } else {
        // Add new replacement
        await carApi.addReplacement(id, replacementFormData);
        toast.success('Replacement added successfully');
      }
      
      // Refresh car details
      fetchCarDetails();
      handleCloseReplacementDialog();
    } catch (error) {
      console.error('Error saving replacement:', error);
      toast.error('Error saving replacement');
    } finally {
      setSaving(false);
    }
  };

  // Open delete replacement dialog
  const handleOpenDeleteReplacementDialog = (replacement) => {
    setReplacementToDelete(replacement);
    setDeleteReplacementDialogOpen(true);
  };

  // Close delete replacement dialog
  const handleCloseDeleteReplacementDialog = () => {
    setDeleteReplacementDialogOpen(false);
    setReplacementToDelete(null);
  };

  // Delete replacement
  const handleDeleteReplacement = async () => {
    if (!replacementToDelete) return;
    
    try {
      setSaving(true);
      await carApi.deleteReplacement(id, replacementToDelete._id);
      toast.success('Replacement deleted successfully');
      fetchCarDetails();
      handleCloseDeleteReplacementDialog();
    } catch (error) {
      console.error('Error deleting replacement:', error);
      toast.error('Error deleting replacement');
    } finally {
      setSaving(false);
    }
  };

  // Get available subtypes for selected type
  const getAvailableSubtypes = () => {
    if (!replacementFormData.type) return [];
    
    const selectedType = replacementTypes.find(type => type.value === replacementFormData.type);
    return selectedType ? selectedType.subtypes : [];
  };

  // Toggle tasks expanded state
  const handleToggleTasks = () => {
    setTasksExpanded(!tasksExpanded);
  };
  
  // Toggle replacements expanded state
  const handleToggleReplacements = () => {
    setReplacementsExpanded(!replacementsExpanded);
  };

  // Function to get icon for replacement type
  const getReplacementTypeIcon = (type) => {
    switch (type) {
      case 'consumables':
        return <SpeedIcon color="primary" />;
      case 'braking':
        return <BrakingIcon color="error" />;
      case 'suspension_chassis':
        return <SuspensionIcon color="secondary" />;
      case 'drivetrain_transmission':
        return <SettingsIcon color="warning" />;
      case 'engine_powertrain':
        return <EngineIcon color="success" />;
      case 'aero_bodywork':
        return <BodyworkIcon color="info" />;
      case 'safety':
        return <SafetyIcon color="error" />;
      case 'electronics_data':
        return <ElectronicsIcon color="primary" />;
      default:
        return <PartsIcon />;
    }
  };

  // Fetch car race history
  const fetchRaceHistory = async () => {
    if (!id) return;
    
    try {
      setLoadingRaceHistory(true);
      const data = await carApi.getRaceHistory(id);
      const raceHistoryData = data.raceHistory || [];
      setRaceHistory(raceHistoryData);
      
      // Update chart data
      if (raceHistoryData.length > 0) {
        setChartData({
          labels: raceHistoryData.map(race => race.title),
          datasets: [
            {
              label: t('carDetail.kilometers'),
              data: raceHistoryData.map(race => race.kilometers),
              fill: false,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1
            },
            {
              label: t('carDetail.cumulativeKilometers'),
              data: raceHistoryData.map(race => race.cumulativeKilometers),
              fill: false,
              borderColor: 'rgb(255, 99, 132)',
              tension: 0.1
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching race history:', error);
      toast.error(t('errors.fetchingRaceHistory'));
    } finally {
      setLoadingRaceHistory(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/cars')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  if (!car) {
    return (
      <Box p={3}>
        <Typography>{t('carDetail.notFound')}</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/cars')}
          sx={{ mt: 2 }}
        >
          {t('common:back')}
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/cars')}
          sx={{ mb: 2 }}
        >
          {t('common:back')}
        </Button>
        
        <Grid container spacing={3}>
          {/* Main */}
          <Grid item xs={12} md={3}>
             {/* Car Details Card */}
             <CarDetailsCard 
               car={car} 
               isCarOwner={isCarOwner} 
               handleImageUpload={handleImageUpload} 
               handleImageError={handleImageError} 
               setEditDialogOpen={setEditDialogOpen} 
               setDeleteDialogOpen={setDeleteDialogOpen} 
             />
          </Grid>
          
          {/* Right */}
          <Grid item xs={12} md={9}>

            
            {/* Tasks Section */}
            <CarTasksSection 
              car={car} 
              tasks={tasks} 
              showCompletedTasks={showCompletedTasks} 
              setShowCompletedTasks={setShowCompletedTasks} 
              setCreateTaskModalOpen={setCreateTaskModalOpen}
              isCarOwner={isCarOwner}
              getStatusIcon={getStatusIcon}
              getPriorityColor={getPriorityColor}
              getPaymentStatusIcon={getPaymentStatusIcon}
              tasksExpanded={tasksExpanded}
              setTasksExpanded={setTasksExpanded}
            />

            {/* Race History Chart */}
            <CarRaceHistory 
              car={car} 
              raceHistory={raceHistory} 
              loadingRaceHistory={loadingRaceHistory} 
              showRaceHistory={showRaceHistory} 
              setShowRaceHistory={setShowRaceHistory}
              chartData={chartData}
              chartOptions={chartOptions}
              setCreateTaskModalOpen={setCreateTaskModalOpen}
              setCheckupTaskMode={setCheckupTaskMode}
            />
            
            {/* Components Status Section */}
            <CarComponentsStatus 
              car={car} 
              showComponents={showComponents} 
              setShowComponents={setShowComponents} 
            />
            
            
            {/* Replacements Section */}
            <CarReplacementsSection 
              car={car} 
              showReplacements={replacementsExpanded} 
              setShowReplacements={setReplacementsExpanded} 
              replacements={car?.replacements || []}
              isCarOwner={isCarOwner}
              handleOpenAddReplacementDialog={handleOpenAddReplacementDialog}
              handleOpenEditReplacementDialog={handleOpenEditReplacementDialog}
              handleOpenDeleteReplacementDialog={handleOpenDeleteReplacementDialog}
              getReplacementTypeIcon={getReplacementTypeIcon}
            />
          </Grid>
        </Grid>
      </Box>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            {t('carDetail.editCar')}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            name="name"
            label={t('carDetail.name')}
            type="text"
            fullWidth
            value={editFormData.name}
            onChange={handleEditChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="model"
            label={t('carDetail.model')}
            type="text"
            fullWidth
            value={editFormData.model || ''}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="year"
            label={t('carDetail.year')}
            type="number"
            fullWidth
            value={editFormData.year || ''}
            onChange={handleEditChange}
            inputProps={{ min: 1900, max: 2100 }}
            sx={{ mb: 3 }}
          />
          <TextField
            margin="dense"
            name="notes"
            label={t('carDetail.notes')}
            multiline
            rows={4}
            fullWidth
            value={editFormData.notes || ''}
            onChange={handleEditChange}
            sx={{ mb: 3 }}
          />
          
          <TextField
            margin="dense"
            name="checkupThreshold"
            label={t('carDetail.checkupThreshold')}
            type="number"
            fullWidth
            value={editFormData.checkupThreshold === '' ? '' : (editFormData.checkupThreshold || 5000)}
            onChange={handleEditChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            helperText={t('carDetail.checkupThresholdHelp')}
            sx={{ mb: 3 }}
          />
          
          {/* Components Accordion */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="components-content"
              id="components-header"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {t('carDetail.componentTitle')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Brake Pads */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t('carDetail.components.brakePads')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.brakePads.lastReplaced"
                        label={t('carDetail.components.lastReplaced')}
                        type="number"
                        fullWidth
                        value={editFormData.components.brakePads.lastReplaced}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.brakePads.lifespan"
                        label={t('carDetail.components.lifespan')}
                        type="number"
                        fullWidth
                        value={editFormData.components.brakePads.lifespan}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="components.brakePads.notes"
                        label={t('carDetail.components.notes')}
                        multiline
                        rows={2}
                        fullWidth
                        value={editFormData.components.brakePads.notes}
                        onChange={handleEditChange}
                        placeholder={t('carDetail.components.notesPlaceholder')}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Brake Discs */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t('carDetail.components.brakeDiscs')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.brakeDiscs.lastReplaced"
                        label={t('carDetail.components.lastReplaced')}
                        type="number"
                        fullWidth
                        value={editFormData.components.brakeDiscs.lastReplaced}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.brakeDiscs.lifespan"
                        label={t('carDetail.components.lifespan')}
                        type="number"
                        fullWidth
                        value={editFormData.components.brakeDiscs.lifespan}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="components.brakeDiscs.notes"
                        label={t('carDetail.components.notes')}
                        multiline
                        rows={2}
                        fullWidth
                        value={editFormData.components.brakeDiscs.notes}
                        onChange={handleEditChange}
                        placeholder={t('carDetail.components.notesPlaceholder')}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Front Tires */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t('carDetail.components.frontTires')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.frontTires.lastReplaced"
                        label={t('carDetail.components.lastReplaced')}
                        type="number"
                        fullWidth
                        value={editFormData.components.frontTires.lastReplaced}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.frontTires.lifespan"
                        label={t('carDetail.components.lifespan')}
                        type="number"
                        fullWidth
                        value={editFormData.components.frontTires.lifespan}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="components.frontTires.notes"
                        label={t('carDetail.components.notes')}
                        multiline
                        rows={2}
                        fullWidth
                        value={editFormData.components.frontTires.notes}
                        onChange={handleEditChange}
                        placeholder={t('carDetail.components.notesPlaceholder')}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Rear Tires */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t('carDetail.components.rearTires')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.rearTires.lastReplaced"
                        label={t('carDetail.components.lastReplaced')}
                        type="number"
                        fullWidth
                        value={editFormData.components.rearTires.lastReplaced}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.rearTires.lifespan"
                        label={t('carDetail.components.lifespan')}
                        type="number"
                        fullWidth
                        value={editFormData.components.rearTires.lifespan}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="components.rearTires.notes"
                        label={t('carDetail.components.notes')}
                        multiline
                        rows={2}
                        fullWidth
                        value={editFormData.components.rearTires.notes}
                        onChange={handleEditChange}
                        placeholder={t('carDetail.components.notesPlaceholder')}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Motor */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t('carDetail.components.motor')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.motor.lastReplaced"
                        label={t('carDetail.components.lastReplaced')}
                        type="number"
                        fullWidth
                        value={editFormData.components.motor.lastReplaced}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="components.motor.lifespan"
                        label={t('carDetail.components.lifespan')}
                        type="number"
                        fullWidth
                        value={editFormData.components.motor.lifespan}
                        onChange={handleEditChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">km</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="components.motor.notes"
                        label={t('carDetail.components.notes')}
                        multiline
                        rows={2}
                        fullWidth
                        value={editFormData.components.motor.notes}
                        onChange={handleEditChange}
                        placeholder={t('carDetail.components.notesPlaceholder')}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            
            <Box 
              sx={{ 
                position: 'relative', 
                width: '100%',
                cursor: 'pointer',
                '&:hover': {
                  '& .overlay': {
                    opacity: 1
                  }
                }
              }}
              onClick={handleImageUpload}
            >
              {editFormData.imageUrl ? (
                <Box sx={{ position: 'relative', mb: 2, width: '100%', textAlign: 'center' }}>
                  <img 
                    src={getTransformedImageUrl(editFormData.imageUrl, 'app')} 
                    alt={editFormData.name}
                    style={{ 
                      maxWidth: '100%', 
                      borderRadius: 8,
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    width: '100%',
                    height: 150,
                    borderRadius: 2,
                    mb: 2
                  }}
                >
                  <CarIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
              <PhotoCameraIcon sx={{ mr: 1 }} />
              <Typography variant="body1">{t('common:upload.carImage')}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            color="primary" 
            variant="contained"
            disabled={saving || !editFormData.name}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? t('common:upload.uploading') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} />
          {t('carDetail.deleteCar')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>{t('carDetail.deleteConfirmation')}</Typography>
          {tasks.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error.dark" fontWeight="bold">
                {t('carDetail.deleteWarning')}
              </Typography>
              <Typography color="error.dark" variant="body2" sx={{ mt: 1 }}>
                {t('tasks')}: {tasks.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteCar} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t('common:actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Task Modal */}
      <CreateTaskModal
        open={createTaskModalOpen}
        onClose={() => {
          setCreateTaskModalOpen(false);
          setCheckupTaskMode(false);
        }}
        onSuccess={handleTaskCreated}
        initialCarId={id}
        isCheckupTask={checkupTaskMode}
      />

      {/* Replacement Dialog */}
      <Dialog open={replacementDialogOpen} onClose={handleCloseReplacementDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReplacementId ? 'Edit Replacement' : 'Add Replacement'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Title"
                fullWidth
                required
                value={replacementFormData.title}
                onChange={handleReplacementFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={replacementFormData.type}
                  onChange={handleReplacementFormChange}
                  label="Type"
                >
                  {loadingReplacementTypes ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    replacementTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!replacementFormData.type}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  name="subtype"
                  value={replacementFormData.subtype}
                  onChange={handleReplacementFormChange}
                  label="Subtype"
                >
                  {getAvailableSubtypes().map((subtype) => (
                    <MenuItem key={subtype.value} value={subtype.value}>
                      {subtype.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="date"
                label="Date"
                type="date"
                fullWidth
                required
                value={replacementFormData.date}
                onChange={handleReplacementFormChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={replacementFormData.notes}
                onChange={handleReplacementFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplacementDialog}>Cancel</Button>
          <Button
            onClick={handleSaveReplacement}
            variant="contained"
            color="primary"
            disabled={saving || !replacementFormData.title || !replacementFormData.type || !replacementFormData.subtype}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Replacement Dialog */}
      <Dialog open={deleteReplacementDialogOpen} onClose={handleCloseDeleteReplacementDialog}>
        <DialogTitle>Delete Replacement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the replacement "{replacementToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteReplacementDialog}>Cancel</Button>
          <Button onClick={handleDeleteReplacement} color="error" variant="contained">
            {saving ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CarDetailPage; 